import React, { useEffect, useState } from "react";
import { Card, List, Spin, Typography, Button, Tag, Space, Empty } from "antd";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import API from "api/endpoints";
import employeeDocumentFolderService from "Services/EmployeeDocumentFolderService";
import { downloadFile } from "Common/downloadFile";
import { DownloadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";

const { Title, Text } = Typography;

// Maps a file role from the folder response to its backend download segment +
// an employee-friendly label. The response uses "HrFinalVerified" but the
// download endpoint segment is "HrFinal" (same mapping as the HR view).
const FILE_ROLES = {
  HrTemplate: { segment: "HrTemplate", label: "HR Template" },
  ApplicantSubmitted: { segment: "ApplicantSubmitted", label: "Your File" },
  HrFinalVerified: { segment: "HrFinal", label: "Final Copy" },
};

const HiringDocumentsPage = () => {
  const [loading, setLoading] = useState(false);
  const [folder, setFolder] = useState(null);
  const [errorText, setErrorText] = useState(null);
  const [downloadingKey, setDownloadingKey] = useState(null);

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        setErrorText(null);
        // GET /api/EmployeeDocumentFolder/me - the logged-in employee's own
        // folder, resolved from the auth token (no employeeId needed).
        const data = await employeeDocumentFolderService.getMine();
        setFolder(data || null);
      } catch (error) {
        // Log the technical detail for debugging; keep the UI calm.
        console.error("Error fetching documents:", error?.response?.data?.details || error);
        // A new / not-yet-processed employee has no folder linked yet, so a
        // not-found-type response is expected - show the empty state, not a red
        // error. Only surface a real error for an unexpected server failure.
        const status = error?.response?.status;
        if (status === 400 || status === 403 || status === 404) {
          setFolder(null);
        } else {
          setErrorText("Unable to load your documents right now. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, []);

  const handleDownload = async (doc, file) => {
    const role = FILE_ROLES[file.fileRole];
    if (!role) {
      toast.warning("This file cannot be downloaded");
      return;
    }
    const key = `${doc.sourceDocumentId}-${file.fileRole}`;
    setDownloadingKey(key);
    try {
      const url = API.hiringDocumentPackage.fileDownload(doc.sourceDocumentId, role.segment);
      const fileName = (file.filePath || "").split("/").pop() || doc.title || "document";
      await downloadFile(url, fileName);
    } catch (e) {
      console.error("Error downloading file:", e);
      toast.error("Failed to download the file");
    } finally {
      setDownloadingKey(null);
    }
  };

  const groups = folder?.sourceGroups || [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card style={{ margin: 24 }}>
        <Title level={4}>My Documents</Title>
        <p>Your HR documents appear here. Use the download button to save a copy.</p>
        {errorText && (
          <div
            style={{
              backgroundColor: "#fff2f0",
              border: "1px solid #ffccc7",
              padding: "10px 15px",
              borderRadius: "8px",
              color: "#ff4d4f",
              marginBottom: "20px",
            }}
          >
            <strong>Error: </strong> {errorText}
          </div>
        )}
        {loading ? (
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <Spin size="large" />
          </div>
        ) : groups.length === 0 ? (
          <Empty description="You don't have any documents yet. They appear here once your paperwork is processed." />
        ) : (
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            {groups.map((group) => (
              <Card
                key={group.employeeDocumentSourceGroupId}
                title={group.title || "Documents"}
                size="small"
                type="inner"
              >
                {group.description && (
                  <p style={{ color: "rgba(0,0,0,0.45)", marginBottom: 12 }}>{group.description}</p>
                )}
                <List
                  size="small"
                  dataSource={group.documents || []}
                  locale={{ emptyText: "No documents in this group" }}
                  renderItem={(doc) => (
                    <List.Item key={doc.sourceDocumentId}>
                      <div style={{ width: "100%" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          <span style={{ fontWeight: 500 }}>{doc.title || doc.documentType}</span>
                          {doc.status && <Tag style={{ marginRight: 0 }}>{doc.status}</Tag>}
                        </div>
                        <div style={{ marginTop: 8 }}>
                          {(doc.files || []).length === 0 ? (
                            <Text type="secondary" style={{ fontSize: "0.8rem" }}>
                              No files
                            </Text>
                          ) : (
                            <Space wrap size="small">
                              {doc.files.map((file) => {
                                const role = FILE_ROLES[file.fileRole];
                                const key = `${doc.sourceDocumentId}-${file.fileRole}`;
                                return (
                                  <Button
                                    key={file.fileRole}
                                    className="btn-primary"
                                    size="small"
                                    icon={<DownloadOutlined />}
                                    loading={downloadingKey === key}
                                    onClick={() => handleDownload(doc, file)}
                                  >
                                    {role ? role.label : file.label || "File"}
                                  </Button>
                                );
                              })}
                            </Space>
                          )}
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </Card>
            ))}
          </Space>
        )}
      </Card>
    </DashboardLayout>
  );
};

export default HiringDocumentsPage;
